import { useQuery, useQueryClient } from '@tanstack/react-query';

import { useThrottledMutation as useMutation } from '@hooks/use-throttled-mutation';
import { useScopedTranslate } from '@locale/translate';

import { handlePossibleNonFieldError } from 'api/query-client';
import { isInTemplateRoute } from 'ui/router';

import {
  createJourneyWebhook,
  deleteJourneyWebhook,
  JourneyWebhook,
  JourneyWebhookData,
  JourneyWebhookList,
  loadJourneyWebhook,
  loadJourneyWebhookLog,
  loadJourneyWebhookLogs,
  loadJourneyWebhooks,
  loadTemplateJourneyWebhook,
  loadTemplateJourneyWebhooks,
  updateJourneyWebhook,
} from './requests';

const getJourneyWebhooksKey = (journeyShortId: string) => ['journey', journeyShortId, 'webhooks'];
const getJourneyWebhookKey = (webhookShortId: string) => ['journey', webhookShortId, 'webhook'];

const useInvalidateJourneyWebhooks = () => {
  const queryClient = useQueryClient();

  const invalidateJourneyWebhooks = (shortId: string) =>
    queryClient.invalidateQueries(getJourneyWebhooksKey(shortId));

  const invalidateJourneyWebhook = (webhookShortId: string) =>
    queryClient.invalidateQueries(getJourneyWebhookKey(webhookShortId));

  return { invalidateJourneyWebhooks, invalidateJourneyWebhook };
};

export const useJourneyWebhooksList = (journeyShortId: string) => {
  const loadFn = isInTemplateRoute() ? loadTemplateJourneyWebhooks : loadJourneyWebhooks;
  return useQuery({
    queryKey: getJourneyWebhooksKey(journeyShortId),
    queryFn: () => loadFn(journeyShortId),
  });
};

export const useJourneyWebhook = (webhookShortId: string) => {
  const loadFn = isInTemplateRoute() ? loadTemplateJourneyWebhook : loadJourneyWebhook;
  return useQuery({
    queryKey: getJourneyWebhookKey(webhookShortId),
    queryFn: () => loadFn(webhookShortId),
  });
};

export const useCreateJourneyWebhook = (journeyShortId: string) => {
  const { invalidateJourneyWebhooks } = useInvalidateJourneyWebhooks();
  return useMutation({
    mutationFn: () => createJourneyWebhook(journeyShortId),
    onSuccess: () => {
      invalidateJourneyWebhooks(journeyShortId);
    },
  });
};

export const useUpdateJourneyWebhook = (webhook: JourneyWebhook | JourneyWebhookList) => {
  const { t } = useScopedTranslate(locale);
  const { invalidateJourneyWebhooks, invalidateJourneyWebhook } = useInvalidateJourneyWebhooks();
  return useMutation({
    mutationFn: (webhookData: Partial<JourneyWebhookData>) =>
      updateJourneyWebhook(webhook.shortId, webhookData),
    onSuccess: () => {
      invalidateJourneyWebhook(webhook.shortId);
      invalidateJourneyWebhooks(webhook.journeyShortId);
    },
    onError: (error: unknown) => {
      handlePossibleNonFieldError(error, t('updateFailed'));
    },
  });
};

export const useDeleteJourneyWebhook = (journeyShortId: string) => {
  const { invalidateJourneyWebhooks } = useInvalidateJourneyWebhooks();
  return useMutation({
    mutationFn: (webhookShortId: string) => deleteJourneyWebhook(webhookShortId),
    onSuccess: () => {
      invalidateJourneyWebhooks(journeyShortId);
    },
  });
};

export const useJourneyWebhookLogs = (webhookShortId: string) => {
  return useQuery({
    queryKey: ['journeys', webhookShortId, 'webhook', 'logs'],
    queryFn: () => loadJourneyWebhookLogs(webhookShortId),
  });
};

export const useJourneyWebhookLog = (webhookShortId: string, webhookLogShortId: string) => {
  return useQuery({
    queryKey: ['journeys', webhookShortId, webhookLogShortId, 'webhook', 'log'],
    queryFn: () => loadJourneyWebhookLog(webhookShortId, webhookLogShortId),
  });
};

const locale = {
  en: {
    updateFailed: 'Failed to save webhook',
  },
};
